import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { AccountService } from "../../account/account.service";
import { Observable, from, of } from "rxjs";
import { map, switchMap, catchError, timeout, shareReplay, take } from "rxjs/operators";
import { FirebaseAuthService } from "../auth/firebase-auth.service";
import { SignupStateService } from "src/app/welcome/services/signup-state.service";
import { LoginContinuationService } from "../auth/login-continuation.service";

@Injectable({
  providedIn: "root",
})
export class ProfileCompleteGuard implements CanActivate, CanActivateChild {
  private accountExit?: { navigation: number; result: Observable<boolean | UrlTree> };
  constructor(
    private accountService: AccountService,
    private router: Router,
    private firebaseAuth: FirebaseAuthService,
    private signupState: SignupStateService,
    private continuation: LoginContinuationService,
  ) {}

  canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Account remains available without a holding, as on the existing /account route.
    if (state?.url.split(/[?#]/)[0] === "/manage/account") return of(true);
    return from(this.firebaseAuth.currentUser).pipe(
      timeout({ first: 5000, with: () => of(null) }),
      catchError(() => of(null)),
      switchMap(user => (user?.emailVerified ? this.checkProfileCompletion() : of(true))),
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (this.router.url.split(/[?#]/)[0] !== "/manage/account") return of(true);
    const navigation = this.router.getCurrentNavigation()?.id;
    if (navigation === undefined) return this.canActivate(route, state);
    // Nested descendants share one check when leaving Account, which permits incomplete profiles.
    if (this.accountExit?.navigation !== navigation) {
      this.accountExit = {
        navigation,
        result: this.canActivate(route, state).pipe(take(1), shareReplay(1)),
      };
    }
    return this.accountExit.result;
  }

  private checkProfileCompletion(): Observable<boolean | UrlTree> {
    return this.accountService.getProfile().pipe(
      switchMap(profile => {
        return this.accountService.getUserHoldings().pipe(
          map(holdings => {
            const filteredHoldings = holdings.filter(holding => holding.holdingName !== "FL/OCK/DEMO");
            if (filteredHoldings.length === 0) {
              this.continuation.remember(this.router.getCurrentNavigation()?.extractedUrl.toString());
              if (!profile?.firstName) {
                this.signupState.updateState({
                  currentStep: 3,
                });
                return this.router.parseUrl("/welcome/signup/user-details");
              }
              this.signupState.updateState({
                currentStep: 4,
              });
              return this.router.parseUrl("/welcome/signup/holding-name");
            }
            return true;
          }),
          catchError(() => of(true)),
        );
      }),
      catchError(() => of(true)),
    );
  }
}
